import React from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import {
    VLearnProgressBar, VLearnStatusChip, isVLearnOverdue, vlearnSurface, vlearnType
} from "../../../components/vlearn/vlearnUi";

/**
 * V-Learning — the teacher dashboard's learner table, as a card list for a phone.
 *
 * ── Why this exists rather than a responsive DataGrid ────────────────────────────────
 *
 * `LearnerProgressTable` renders a `DataGrid` inside `<Box sx={{ minWidth: 992 }}>`. That is
 * the right call on a desktop — nine columns genuinely need the room — and it is honest about
 * it: the scroll is on the wrapper, so the table never widens the document. But on a 360px
 * phone it means a third of one column is visible and reading a single learner's row is a
 * 992px horizontal drag, during which the column headers scroll away and the grid's own
 * pagination footer is off to the right where nobody finds it.
 *
 * A table that has to be dragged sideways to be read is not a table on a phone, so this is a
 * card per learner instead — the pattern `MobileWorkStoppagesList` and `MobileTimesheetList`
 * already use in this codebase. The card leads with who and how far, because that is what the
 * manager opened the dashboard to find out; the quiz and date detail sit underneath, and the
 * whole card opens the same `CollabDetailDialog` the desktop row does.
 *
 * ── Presentational, on purpose ───────────────────────────────────────────────────────
 *
 * Every filter, the debounce, the request and the paging state stay in
 * `LearnerProgressTable`, which owns them on both layouts. This component takes the page it
 * is given and draws it. Duplicating the fetch here would be two components disagreeing about
 * what page 2 is.
 *
 * It sits under `containers/vlearn/mobile/` because that is where this codebase keeps its
 * phone variants (`containers/collabs/mobile/MobileWorkStoppagesList`,
 * `containers/reports/mobile/MobileReportsList`…), and being found next to its siblings
 * matters more than the fact that it holds no state of its own.
 *
 * @param {Array}    rows                  the current page of learner rows, server-shaped
 * @param {number}   total                 the row count BEFORE paging, for the pager
 * @param {Object}   paginationModel       `{ page, pageSize }`, zero-based like the DataGrid's
 * @param {Function} onPaginationModelChange
 * @param {Function} onRowOpen             called with an `id_collab`
 * @param {Function} formatDate            `(value, withTime) => string`, the table's own
 * @param {string}   lang
 */
export default function MobileLearnerProgressList({
    rows, total, paginationModel, onPaginationModelChange, onRowOpen,
    formatDate, lang
}) {

    const list = rows || []
    const pageSize = (paginationModel && paginationModel.pageSize) || 25
    const page = (paginationModel && paginationModel.page) || 0
    const pageCount = Math.max(1, Math.ceil((Number(total) || 0) / pageSize))

    /*
     * No skeleton branch, deliberately.
     *
     * `dashboardLearners` is initialised to `[]` and the slice exposes no in-flight flag, so
     * there is no honest way to tell "still loading" from "genuinely empty" here. The desktop
     * DataGrid makes the same call — it carries no `loading` prop either — and the module's
     * thunks take the application's global spinner during the fetch. Inventing a flag that
     * says "loading" when it actually means "empty" would be worse than saying nothing.
     */
    if (!list.length) {
        return (
            <Box sx={{ ...vlearnSurface.card, padding: '24px', textAlign: 'center' }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_NO_ROWS" />
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>

            {list.map((row) => {
                const percent = Number(row.progress_percent) || 0
                const overdue = row.is_overdue || isVLearnOverdue(row.due_date, row.status)
                const photoUrl = NetworkManager.getInstance()
                    .getCollabPhotoUrl(row.id_collab, row.photo_updated_at)
                const initials = [row.prenom, row.nom].filter(Boolean)
                    .map((part) => String(part).charAt(0).toUpperCase()).join('')
                // A NULL `team_name` means the person was targeted by name rather than
                // through a team — a different fact from "has no team", so it keeps the
                // table's own label rather than becoming an em dash.
                const teamLabel = row.team_name || Localize("VLEARN_TEAM_INDIVIDUAL", lang)
                const name = [row.prenom, row.nom].filter(Boolean).join(' ')

                return (
                    /*
                     * The whole card is the target, not a 30px chevron in its corner: on a
                     * phone the affordance has to be the thing the thumb already lands on.
                     * `ButtonBase` rather than an `onClick` Box so it is focusable, activated
                     * by Enter and Space, and announced as a button.
                     */
                    <ButtonBase
                        key={row.id_collab}
                        onClick={() => onRowOpen && onRowOpen(row.id_collab)}
                        aria-label={`${name} — ${Localize("VLEARN_OPEN_COLLAB_DETAIL", lang)}`}
                        sx={{
                            ...vlearnSurface.card,
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '14px 14px 12px',
                            '&:focus-visible': {
                                outline: '2px solid var(--orange, rgb(255,144,21))',
                                outlineOffset: '2px'
                            }
                        }}
                    >
                        {/* ── Who, and where they stand ──────────────────────────── */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <Avatar
                                src={photoUrl || undefined}
                                sx={{
                                    width: 38, height: 38, fontSize: 13, flexShrink: 0,
                                    bgcolor: 'var(--lightGrey, #ededed)', color: '#6f6f6f'
                                }}
                            >
                                {initials}
                            </Avatar>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography noWrap sx={{ ...vlearnType.cardTitle }}>{name}</Typography>
                                <Typography noWrap sx={{ ...vlearnType.meta }}>{teamLabel}</Typography>
                            </Box>

                            <ChevronRightRoundedIcon
                                aria-hidden="true"
                                sx={{ fontSize: 20, color: 'var(--text-muted, #8b92a0)', flexShrink: 0 }}
                            />
                        </Box>

                        {/* ── Progress: the number the dashboard was opened for ───── */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <VLearnProgressBar percent={percent} status={row.status} />
                            </Box>
                            <Typography sx={{
                                fontSize: '0.82rem', fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                color: 'var(--text-primary, #111318)'
                            }}>
                                {`${percent}%`}
                            </Typography>
                        </Box>

                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            flexWrap: 'wrap', mt: 1
                        }}>
                            <VLearnStatusChip status={row.status} overdue={overdue} />
                            <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                <Langs str="VLEARN_COL_LESSONS_DONE" />
                                {` ${Number(row.lessons_completed) || 0} / ${Number(row.lessons_total) || 0}`}
                            </Typography>
                        </Box>

                        {/* ── The rest, as labelled pairs ─────────────────────────── */}
                        <Box sx={{
                            mt: 1.25, pt: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)',
                            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px'
                        }}>
                            <Field labelKey="VLEARN_COL_BEST_QUIZ">
                                {/* NULL is "not graded", which is not the statement 0% makes. */}
                                {row.best_quiz_percent === null
                                    || typeof row.best_quiz_percent === 'undefined'
                                    ? '—'
                                    : `${Math.round(Number(row.best_quiz_percent) * 10) / 10}%`}
                            </Field>

                            <Field labelKey="VLEARN_COL_QUIZ_PASSED">
                                <QuizPassed value={row.quiz_passed} lang={lang} />
                            </Field>

                            <Field labelKey="VLEARN_COL_LAST_ACCESS">
                                {formatDate ? formatDate(row.last_accessed_at, true) : '—'}
                            </Field>

                            <Field labelKey="VLEARN_COL_DUE_DATE" danger={overdue}>
                                {formatDate ? formatDate(row.due_date, false) : '—'}
                            </Field>
                        </Box>
                    </ButtonBase>
                )
            })}

            {/* ── Paging ──────────────────────────────────────────────────────────
                MUI's `Pagination` rather than the DataGrid's own footer, which is inside the
                992px scroller and therefore off-screen on a phone. Page size is not offered
                here: three choices of page length is a desk decision, and the row that held
                it is the row with no space. */}
            {pageCount > 1 ? (
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 1, mt: 1
                }}>
                    <Typography sx={{ ...vlearnType.meta }}>
                        <Langs
                            str="VLEARN_SHOWING_COUNT"
                            params={{ shown: String(list.length), total: String(total || 0) }}
                        />
                    </Typography>
                    <Pagination
                        count={pageCount}
                        // MUI's Pagination is 1-based, the DataGrid's model is 0-based.
                        page={page + 1}
                        onChange={(event, value) => onPaginationModelChange
                            && onPaginationModelChange({ page: value - 1, pageSize })}
                        color="primary"
                        siblingCount={0}
                        // `size="medium"` gives a 40px button; the sx floor takes it to the
                        // 44px tap target, which `size="small"` (26px) never reaches.
                        sx={{ '& .MuiPaginationItem-root': { minWidth: 44, height: 44 } }}
                    />
                </Box>
            ) : null}
        </Box>
    )
}

/** One labelled value in the card's detail grid. */
function Field({ labelKey, children, danger }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
                ...vlearnType.meta, textTransform: 'uppercase',
                letterSpacing: '.04em', fontWeight: 600, fontSize: '0.66rem'
            }}>
                <Langs str={labelKey} />
            </Typography>
            <Typography noWrap sx={{
                fontSize: '0.8rem',
                fontWeight: danger ? 700 : 500,
                fontVariantNumeric: 'tabular-nums',
                color: danger ? '#B3271F' : 'var(--text-primary, #111318)'
            }}>
                {children}
            </Typography>
        </Box>
    )
}

/**
 * The pass/fail marker.
 *
 * The word is printed beside the icon rather than left to colour and shape alone — the
 * desktop grid hangs that meaning on a Tooltip, and a tooltip does not exist on touch.
 */
function QuizPassed({ value, lang }) {
    if (value === null || typeof value === 'undefined') {
        return <Box component="span">—</Box>
    }
    const passed = Boolean(Number(value))
    const label = Localize(passed ? "VLEARN_YES" : "VLEARN_NO", lang)
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {passed
                ? <CheckCircleRoundedIcon aria-hidden="true"
                    sx={{ fontSize: 16, color: 'var(--green, rgb(58,156,96))' }} />
                : <RemoveCircleOutlineRoundedIcon aria-hidden="true"
                    sx={{ fontSize: 16, color: '#9aa0ad' }} />}
            {label}
        </Box>
    )
}
